import React from 'react';
import { Layers, ChevronRight, Film, Tv } from 'lucide-react';
import { CuratedCollection, MediaItem } from '../types';

interface CollectionCardProps {
  collection: CuratedCollection;
  catalog: MediaItem[];
  onClick: () => void;
  aspectRatio?: 'poster' | 'landscape';
}

export const CollectionCard: React.FC<CollectionCardProps> = ({
  collection,
  catalog,
  onClick,
  aspectRatio = 'poster'
}) => {
  // Find representative poster
  const colItems = catalog.filter(item => 
    item.collectionId === collection.id ||
    item.collectionId === collection.slug ||
    collection.itemIds?.includes(item.id) ||
    item.collections?.includes(collection.id) ||
    item.collections?.includes(collection.slug)
  );

  const representativePoster = collection.bannerImage || colItems.find(i => Boolean(i.poster))?.poster;
  const seriesCount = colItems.filter(i => i.type === 'series').length || collection.seriesCount || 0;
  const moviesCount = colItems.filter(i => i.type === 'movie').length || collection.moviesCount || 0;
  const totalCount = colItems.length || collection.totalItems || (seriesCount + moviesCount);

  return (
    <div
      id={`collection-card-${collection.id}`}
      role="button"
      tabIndex={0}
      data-focusable="true"
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className="group relative cursor-pointer outline-none select-none text-left transition-all duration-300 transform-gpu focus:scale-[1.03] hover:scale-[1.03]"
    >
      {/* Poster Container */}
      <div 
        className={`relative w-full rounded-2xl overflow-hidden bg-slate-900 border border-white/10 group-hover:border-sky-400/60 group-focus:border-sky-400 group-focus:ring-2 group-focus:ring-sky-400 shadow-lg group-hover:shadow-sky-500/20 transition-all ${
          aspectRatio === 'poster' ? 'aspect-[2/3]' : 'aspect-video'
        }`}
      >
        {representativePoster ? (
          <img
            src={representativePoster}
            alt={collection.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center p-4 bg-gradient-to-br from-slate-900 via-sky-950/40 to-slate-900 text-sky-400">
            <div className="p-4 rounded-2xl bg-sky-500/10 border border-sky-500/20 mb-3 group-hover:scale-110 transition-transform">
              <Layers className="w-10 h-10" />
            </div>
            <span className="text-xs font-bold text-center text-slate-300 line-clamp-2">
              {collection.title}
            </span>
          </div>
        )}

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent pointer-events-none" />

        {/* Top Badges */}
        <div className="absolute top-2.5 left-2.5 right-2.5 flex items-center justify-between gap-1 pointer-events-none">
          <span className="px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wider bg-sky-500 text-black shadow-md flex items-center gap-1">
            <Layers className="w-3 h-3" />
            <span>Col·lecció</span>
          </span>
          <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-black/70 backdrop-blur-md text-white border border-white/15">
            {totalCount} títol{totalCount !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Bottom Details inside Poster */}
        <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 text-white pointer-events-none">
          <h3 className="font-display font-bold text-sm sm:text-base leading-tight drop-shadow-md text-white group-hover:text-sky-300 transition-colors line-clamp-2">
            {collection.title}
          </h3>
          <div className="flex items-center gap-2 mt-1.5 text-[11px] text-slate-300 font-medium">
            {seriesCount > 0 && (
              <span className="flex items-center gap-1">
                <Tv className="w-3 h-3 text-sky-400" />
                <span>{seriesCount} sèrie{seriesCount !== 1 ? 's' : ''}</span>
              </span>
            )}
            {seriesCount > 0 && moviesCount > 0 && <span>•</span>}
            {moviesCount > 0 && (
              <span className="flex items-center gap-1">
                <Film className="w-3 h-3 text-cyan-400" />
                <span>{moviesCount} pel·li{moviesCount !== 1 ? 's' : ''}</span>
              </span>
            )}
          </div>
        </div>

        {/* Action arrow icon on hover */}
        <div className="absolute bottom-3 right-3 w-7 h-7 rounded-full bg-sky-500 text-black flex items-center justify-center opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity shadow-lg">
          <ChevronRight className="w-4 h-4" />
        </div>
      </div>
    </div>
  );
};
